import { Tone } from '@gokrew/models/ui.types';
import { Chip } from '@gokrew/ui';
import { formatDate } from '@gokrew/ui/utility/utility';
import type { AllocationTypeResponse } from '../leave-management';
import type {
  LeaveAllocationState,
  LeaveAllocationMethod,
  LeavePolicyPreview,
  LeaveTypePreview,
} from '@/types/employee';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { TranslationNS } from '@gokrew/models/constants';
import { useTranslation } from 'react-i18next';

interface LeaveEligibilityPreviewProps {
  policies: LeavePolicyPreview[];
  isLoading?: boolean;
  allocationState: LeaveAllocationState;
  setAllocationState: React.Dispatch<
    React.SetStateAction<LeaveAllocationState>
  >;
}
const allocationLabels: Record<AllocationTypeResponse, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  yearly: 'Yearly',
  one_time: 'One Time',
};

const cycleLabels: Record<AllocationTypeResponse, string> = {
  monthly: 'month',
  quarterly: 'quarter',
  half_yearly: 'half-year',
  yearly: 'year',
  one_time: 'assignment',
};

export function LeaveEligibilityPreview({
  policies,
  isLoading,
  allocationState,
  setAllocationState,
}: LeaveEligibilityPreviewProps) {
  const { t } = useTranslation(TranslationNS.Employee);
  const handleMethodChange = (
    leaveTypeId: string,
    method: LeaveAllocationMethod
  ) => {
    setAllocationState((prev) => ({
      ...prev,
      [leaveTypeId]: {
        method,
        customValue:
          method === 'CUSTOM' ? prev[leaveTypeId]?.customValue : undefined,
      },
    }));
  };

  const handleCustomValueChange = (leaveTypeId: string, value: number) => {
    setAllocationState((prev) => ({
      ...prev,
      [leaveTypeId]: {
        ...prev[leaveTypeId],
        method: 'CUSTOM',
        customValue: value,
      },
    }));
  };

  const getDefaultMethod = (
    leaveType: LeaveTypePreview
  ): LeaveAllocationMethod => {
    return allocationState[leaveType.id]?.method ?? 'NONE';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center space-x-2 py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        <p className="text-gray-600">{t('leave_preview.calculating')}</p>
      </div>
    );
  }

  if (!policies || policies.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-gray-500">{t('leave_preview.no_policies')}</p>
      </div>
    );
  }

  return (
    <Accordion
      type="multiple"
      defaultValue={policies.map((p) => p.id)}
      className="space-y-4"
    >
      {policies.map((policy) => (
        <AccordionItem key={policy.id} value={policy.id}>
          <AccordionTrigger>
            <h4 className="font-semibold text-gray-900">{policy.name}</h4>
          </AccordionTrigger>

          <AccordionContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {policy.leaveTypes.map((leaveType) => {
                const selectedMethod = getDefaultMethod(leaveType);
                const customValue = allocationState[leaveType.id]?.customValue;

                return (
                  <div
                    key={leaveType.id}
                    className="rounded-lg border bg-white p-5 shadow-sm transition-shadow"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-6">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">
                            {leaveType.name}
                          </span>
                          <Chip type={Tone.SUCCESS}>
                            {allocationLabels[leaveType.allocationType]}
                          </Chip>
                        </div>
                        <div className="tight">
                          <p className="text-sm text-gray-500">
                            {leaveType.allocationType === 'one_time'
                              ? 'Credited once upon assignment'
                              : `Credits ${allocationLabels[leaveType.allocationType].toLowerCase()}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            {leaveType.nextScheduleDate &&
                              `Next: ${formatDate(leaveType.nextScheduleDate)}`}
                          </p>
                        </div>

                        {leaveType.proRata ? (
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-700">
                              {t('leave_preview.text.pro_rata')}:{' '}
                              <span className="font-bold">
                                {leaveType.proRata.leaves}
                              </span>{' '}
                              {t('leave_preview.text.leaves')}
                            </p>
                            <p className="text-xs text-gray-500">
                              {t('leave_preview.text.calculated_from')}{' '}
                              {formatDate(leaveType.proRata.calculatedFrom)}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm font-medium text-gray-700">
                            {t('leave_preview.text.total')}:{' '}
                            <span className="font-bold">
                              {leaveType.totalLeaves}
                            </span>{' '}
                            {t('leave_preview.text.leaves')}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input
                              type="radio"
                              name={`allocation-${leaveType.id}`}
                              value="PRO_RATA"
                              checked={selectedMethod === 'PRO_RATA'}
                              onChange={() =>
                                handleMethodChange(leaveType.id, 'PRO_RATA')
                              }
                              disabled={!leaveType.proRata}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <span
                              className={`text-sm ${!leaveType.proRata ? 'text-gray-400' : 'text-gray-700 group-hover:text-gray-900'}`}
                            >
                              {t('leave_preview.labels.prorata')}
                            </span>
                          </label>

                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input
                              type="radio"
                              name={`allocation-${leaveType.id}`}
                              value="FULL"
                              checked={selectedMethod === 'FULL'}
                              onChange={() =>
                                handleMethodChange(leaveType.id, 'FULL')
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900">
                              {t('leave_preview.labels.full')}
                            </span>
                          </label>

                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input
                              type="radio"
                              name={`allocation-${leaveType.id}`}
                              value="CUSTOM"
                              checked={selectedMethod === 'CUSTOM'}
                              onChange={() =>
                                handleMethodChange(leaveType.id, 'CUSTOM')
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900">
                              {t('leave_preview.labels.custom')}
                            </span>
                          </label>

                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input
                              type="radio"
                              name={`allocation-${leaveType.id}`}
                              value="NONE"
                              checked={selectedMethod === 'NONE'}
                              onChange={() =>
                                handleMethodChange(leaveType.id, 'NONE')
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900">
                              {t('leave_preview.labels.none')}
                            </span>
                          </label>
                        </div>

                        {selectedMethod === 'CUSTOM' && (
                          <div className="space-y-1">
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              placeholder={t(
                                'leave_preview.labels.enter_custom'
                              )}
                              value={customValue ?? ''}
                              onChange={(e) =>
                                handleCustomValueChange(
                                  leaveType.id,
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            <p className="text-xs text-gray-500">
                              {t('leave_preview.labels.custom_hint')}
                            </p>
                          </div>
                        )}

                        {selectedMethod === 'FULL' && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                            <p className="text-xs text-blue-800">
                              {t('leave_preview.text.will_credit')}{' '}
                              <span className="font-bold">
                                {leaveType.totalLeaves}
                              </span>{' '}
                              {t('leave_preview.text.on_next')}{' '}
                              {cycleLabels[leaveType.allocationType]}{' '}
                              {t('leave_preview.text.cycle')}
                            </p>
                          </div>
                        )}

                        {selectedMethod === 'PRO_RATA' && leaveType.proRata && (
                          <div className="bg-green-50 border border-green-200 rounded-md p-2">
                            <p className="text-xs text-green-800">
                              {t('leave_preview.text.will_credit')}{' '}
                              <span className="font-bold">
                                {leaveType.proRata.leaves}
                              </span>{' '}
                              {t('leave_preview.text.pro_rated')}
                            </p>
                          </div>
                        )}

                        {selectedMethod === 'NONE' && (
                          <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                            <p className="text-xs text-gray-600">
                              {t('leave_preview.labels.none_hint')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
